'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import type { LiquidityPosition } from './useLiquidityPositions';
import type { TokenConfigType } from '@/constants/types/currency';
import { ChainType } from '@/constants/chain-specific';
import { useNativeTokenHandler } from './useNativeTokenHandler';
import { Address } from 'viem';

/**
 * Hook to find existing liquidity positions for a specific token pair
 */
export function useExistingPosition(token0, token1, dexSlug, positions, t0) {
  const $ = _c(21);
  const chainId = t0 === undefined ? 109 : t0;
  const {
    getEffectiveToken
  } = useNativeTokenHandler(chainId);
  let t1;
  bb0: {
    if (!token0 || !token1 || !dexSlug || !positions || positions.length === 0) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== dexSlug || $[1] !== getEffectiveToken || $[2] !== token0) {
      t2 = getEffectiveToken(token0, dexSlug) || token0;
      $[0] = dexSlug;
      $[1] = getEffectiveToken;
      $[2] = token0;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const effectiveToken0 = t2;
    let t3;
    if ($[4] !== dexSlug || $[5] !== getEffectiveToken || $[6] !== token1) {
      t3 = getEffectiveToken(token1, dexSlug) || token1;
      $[4] = dexSlug;
      $[5] = getEffectiveToken;
      $[6] = token1;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const effectiveToken1 = t3;
    let t4;
    if ($[8] !== effectiveToken0.address) {
      t4 = effectiveToken0.address.toLowerCase();
      $[8] = effectiveToken0.address;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    const token0Address = t4 as Address;
    let t5;
    if ($[10] !== effectiveToken1.address) {
      t5 = effectiveToken1.address.toLowerCase();
      $[10] = effectiveToken1.address;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    const token1Address = t5 as Address;
    let t6;
    if ($[12] !== dexSlug || $[13] !== positions || $[14] !== token0Address || $[15] !== token1Address) {
      let t7;
      if ($[17] !== dexSlug || $[18] !== token0Address || $[19] !== token1Address) {
        t7 = position => {
          const posToken0 = position.token0.address.toLowerCase() as Address;
          const posToken1 = position.token1.address.toLowerCase() as Address;
          const tokensMatch = posToken0 === token0Address && posToken1 === token1Address || posToken0 === token1Address && posToken1 === token0Address;
          const dexMatches = position.dex === dexSlug;
          return tokensMatch && dexMatches;
        };
        $[17] = dexSlug;
        $[18] = token0Address;
        $[19] = token1Address;
        $[20] = t7;
      } else {
        t7 = $[20];
      }
      t6 = positions.find(t7);
      $[12] = dexSlug;
      $[13] = positions;
      $[14] = token0Address;
      $[15] = token1Address;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    const existingPosition = t6;
    t1 = existingPosition || null;
  }
  return t1;
}

/**
 * Hook to get all existing positions for specific tokens (regardless of pairing)
 */
export function useTokenPositions(token, positions, t0) {
  const $ = _c(12);
  const chainId = t0 === undefined ? 109 : t0;
  const {
    getEffectiveToken
  } = useNativeTokenHandler(chainId);
  let t1;
  bb0: {
    if (!token || !positions || positions.length === 0) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== token.address) {
      t2 = token.address.toLowerCase();
      $[1] = token.address;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const nativeAddress = t2 as Address;
    let t3;
    if ($[3] !== getEffectiveToken || $[4] !== nativeAddress || $[5] !== positions || $[6] !== token) {
      let t4;
      if ($[8] !== getEffectiveToken || $[9] !== nativeAddress || $[10] !== token) {
        t4 = position => {
          const posToken0 = position.token0.address.toLowerCase() as Address;
          const posToken1 = position.token1.address.toLowerCase() as Address;
          if (posToken0 === nativeAddress || posToken1 === nativeAddress) {
            return true;
          }
          const effectiveToken = getEffectiveToken(token, position.dex);
          if (effectiveToken && effectiveToken.address.toLowerCase() !== nativeAddress) {
            const wrappedAddress = effectiveToken.address.toLowerCase() as Address;
            return posToken0 === wrappedAddress || posToken1 === wrappedAddress;
          }
          return false;
        };
        $[8] = getEffectiveToken;
        $[9] = nativeAddress;
        $[10] = token;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      t3 = positions.filter(t4);
      $[3] = getEffectiveToken;
      $[4] = nativeAddress;
      $[5] = positions;
      $[6] = token;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t1 = t3;
  }
  return t1;
}